import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, Nav, NavItem, NavLink, CardBody, Button, Col, Input, Row, Label, ModalBody, Form, TabPane, TabContent, Spinner, FormFeedback, FormGroup, Alert, CardFooter, Modal, ModalHeader } from 'reactstrap';
import OSelect from '../../../Components/OElements/OSelect';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { mimeTypeToIconMapping } from '../../../helpers/mime-type_to_icon';
import DistancesAndElevationModal from '../Modals/DistancesAndElevationModal';
import DeleteGridItemModal from '../Modals/DeleteGridItemModal';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { addNewDistanceElevation, updateDistanceElevation, deleteDistanceElevation, getDistanceUnitList } from '../../../slices/thunks';
import { toast } from 'react-toastify';

const selectorDistanceUnit = createSelector(
	state => state.DistanceUnit,
	data => data.itemDistanceUnitList || []
);

const ListDistancesAndElevation = ({ distanceElevationList, setDistanceElevationList, formIsEdit, waterRightId, intakeCoordinatesList = [], restitutionCoordinatesList = [] }) => {
	const dispatch = useDispatch();
	const distanceUnitList = useSelector(selectorDistanceUnit);

	const getDistanceUnitName = (id) => {
		if (!id) return '-';
		const item = distanceUnitList.find(i => i._id === id);
		return item?.name || id || '-';
	};

    const [dataPhotographicRecord, setDataPhotographicRecord] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [modal, setModal] = useState(false);
    const [toggleTab, setToggleTab] = useState(null);
    const [modalDelete, setModalDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		dispatch(getDistanceUnitList());
	}, [dispatch]);

	// Get current user from localStorage
	const getCurrentUser = () => {
		try {
			const authUser = JSON.parse(localStorage.getItem('authUser'));
			return authUser?.user?.email || authUser?.email || 'system';
		} catch (error) {
			console.error('Error getting current user:', error);
			return 'system';
		}
	};

	// Handle Save
	const handleSave = async (formData) => {
		try {
			const currentUser = getCurrentUser();

			if (!formIsEdit) {
				// CREATE mode: no API calls, keep in local state until form is submitted
				if (isEdit && dataPhotographicRecord) {
					// Update existing local item by reference
					setDistanceElevationList(prevList =>
						prevList.map(item => item === dataPhotographicRecord ? { ...formData } : item)
					);
				} else {
					// Add new item to local state
					setDistanceElevationList(prevList => [...prevList, { ...formData }]);
				}
			} else if (isEdit && dataPhotographicRecord?._id) {
				// EDIT mode: update existing item in API
				const updatePayload = {
					...formData,
					_id: dataPhotographicRecord._id,
					modification_user: currentUser,
					modification_date: new Date().toISOString()
				};

				const response = await dispatch(updateDistanceElevation(updatePayload)).unwrap();

				// Update local state with response from API
				setDistanceElevationList(prevList =>
					prevList.map(item =>
						item._id === dataPhotographicRecord._id ? response : item
					)
				);
			} else {
				// EDIT mode: add new item to API immediately with water_right_id
				const newPayload = {
					...formData,
					water_right_id: waterRightId,
					creation_user: currentUser,
					creation_date: new Date().toISOString()
				};

				const response = await dispatch(addNewDistanceElevation(newPayload)).unwrap();

				// Add to local state
				setDistanceElevationList(prevList => [...prevList, response]);
			}
			handleToggleModal();
		} catch (error) {
			console.error('Error saving distance elevation:', error);
			// Toast notification is already handled in the thunk
		}
	};

	// Delete
	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			if (dataPhotographicRecord?._id) {
				// Has _id: saved in the database, delete from API
				await dispatch(deleteDistanceElevation(dataPhotographicRecord)).unwrap();
				setDistanceElevationList(prevList =>
					prevList.filter(item => item._id !== dataPhotographicRecord._id)
				);
			} else {
				// No _id: local-only item (CREATE mode), just remove from state
				setDistanceElevationList(prevList =>
					prevList.filter(item => item !== dataPhotographicRecord)
				);
			}
			handleCloseDeleteModal();
		} catch (error) {
			console.error('Error deleting distance elevation:', error);
			// Toast notification is already handled in the thunk
		} finally {
			setIsDeleting(false);
		}
	};

    // Update Data
    const handleClickEdit = residueCollected => {
		setIsEdit(true);
        setDataPhotographicRecord(residueCollected);
        setModal(true);
    };
    
    // Delete Data
    const handleClickDelete = photographicRecord => {
        setDataPhotographicRecord(photographicRecord);
        setModalDelete(true);
    };

    function handleToggleModal() {
        setModal(prev => !prev);
    }

    function handleCloseDeleteModal() {
        setModalDelete(false);
    }
    
    const columns = useMemo(
        () => [
            {
                header: 'Acciones', enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false, enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false,
                accessorKey: 'residue',
                columnDefType: 'display',
                 size: 'auto',
                Cell: cellProps => {
                    return (
                        <React.Fragment>
                            <div className='d-flex justify-content-center'> 
                                <div className='flex-shrink-0'>
                                    <ul className='list-inline mb-0'>
                                        <li className='list-inline-item'>
                                            <a
                                                title={'Editar'}
                                                href='#'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClickEdit(cellProps.row.original);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
                                            </a>
                                        </li>
                                        <li className='list-inline-item'>
                                            <a
                                                title={'Eliminar'}
                                                href='#'
                                                className='remove-item-btn'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClickDelete(cellProps.row.original);
                                                }}
                                            >
                                                <i className='ri-delete-bin-line align-bottom me-2 text-muted'></i>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                },
            },
            { header: 'Distancia', accessorKey: 'distance',  size: 'auto' },
            { header: 'Unidad Distancia', accessorKey: 'distance_unit',  size: 'auto',
                Cell: ({ row }) => {
                    const val = row.original.distance_unit;
                    return val?.name || getDistanceUnitName(typeof val === 'string' ? val : val?._id) || '-';
                }
            },
            { header: 'Desnivel (m)', accessorKey: 'elevation_difference', size: 'auto',
                Cell: ({ row }) => row.original.elevation_difference ?? row.original.elevation ?? '-'
            },
        ],
        [handleClickEdit]
    );

    return (
        <React.Fragment>
            <Col lg={12}>
                <div className='d-flex align-items-center'>
                    <h5 className='card-title mb-0 flex-grow-1 '>
                        Lista de Distancias y Desnivel
                    </h5>
                    <div className='flex-shrink-0'>
                        <div className='d-flex flex-wrap gap-2'>
                            <button
                                className='btn btn-label btn-primary'
                                onClick={() => {
                                    setIsEdit(false);
                                    setDataPhotographicRecord({});
                                    handleToggleModal();
                                }}
                                type='button'
                            >
                                <i className='ri-add-line label-icon'></i> Añadir Distancias y Desnivel
                            </button>
                        </div>
                    </div>
                </div>
                <div className='mt-3 my-4'>
                    <ODataGrid
                        columns={columns}
                        data={distanceElevationList || []}
                    />
                </div>
            </Col>
            <DistancesAndElevationModal
                dataDistanceElevation={dataPhotographicRecord}
                isOpen={modal}
                isEdit={isEdit}
                handleToggleModal={handleToggleModal}
                handleSave={handleSave}
                waterRightId={waterRightId}
                intakeCoordinatesList={intakeCoordinatesList}
                restitutionCoordinatesList={restitutionCoordinatesList}
            />
			
			<DeleteGridItemModal
				isOpen={modalDelete}
				title='¡Estás a punto de eliminar una Distancia y Desnivel!'
				description='Si deseas realizar esta acción debes hacer click en confirmar.'
				details={dataPhotographicRecord && (
					<div className='small text-muted'>
						<p><strong>Distancia:</strong> {dataPhotographicRecord.distance} {dataPhotographicRecord.distance_unit}</p>
						<p><strong>Desnivel:</strong> {dataPhotographicRecord.elevation} m</p>
					</div>
				)}
				onCancel={handleCloseDeleteModal}
				onConfirm={handleDelete}
				isDeleting={isDeleting}
			/>
        </React.Fragment>
    )
}

export default ListDistancesAndElevation;